/// <reference path="../typedefs/dash.js" />

import * as actions from 'actions/dash'
import { GET_TRUCK_DETAILS } from 'actions/truck'
import { fromJS, Map, List } from 'immutable'
import {
  merge,
  set,
  removeIn,
  setIn,
  get,
  getIn,
  safeToJS,
  pickToJS,
  getInIncludes,
  has,
  getOr,
} from 'utils/immutable'
import {
  assoc,
  prop,
  omit,
  compose,
  path,
  propOr,
  length,
  keys,
  values,
} from 'ramda'
import { isArray, isString } from 'utils/types'
import { resolveDashDefinition } from 'components/SwitchAndGauge/definitions/resolveDashDefinition'
import { resolveOverheadSleeperDefinitions } from 'components/SwitchAndGauge/definitions/resolveOverheadSleeperDefinitions'
import {
  isUnassignedComponent,
  createBlockedList,
  isStagedComponent,
} from 'components/SwitchAndGauge/utils'
import { makeRandId } from 'utils/identity'
import {
  UNASSIGNED_PANEL_PART,
  MSF_CAVITY_PLUG_PART,
  RHD_43N_38N_CODE,
  POCKET,
  MSF_POCKET_LOCATIONS_MAP,
  HARDWIRED_COMPONENT,
  MSF,
  SMART_SWITCH,
  SWITCH_DETAIL_BY_MODEL_CODE,
  SWITCH,
  PLUG,
  LOCATION_TYPE_TBB_SWITCH,
  TBB,
  AUX_PANEL_LOCATION_ID,
  TEM_PANEL_LOCATION_ID,
  LOCATION_TYPE_SINGLE_SWITCH_MSF,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import {
  ERROR,
  TRUCK_SUBTYPE_42N,
  CHEC_PLUS_VEHICLE_SUBTYPES,
} from 'utils/constants'
import { allowedLocationsRulesEngine } from 'rules/dash/allowedLocationsRules/engine'
import { isDashTypeBus } from 'utils/dash'
import { createReducer } from './utils'

export const DASH_REDUX_KEY = 'dash'

// NOTE: sessionStorage for pending changes has been removed.
// The API's pendingEdexUpdate flag is the single source of truth for pending changes.
// After save, the backend tracks which components need DiagnosticLink programming.

/**
 * Get pending changes for the config object in successDashConfig.
 * On page load/refresh, returns empty - the API's pendingEdexUpdate flag is the source of truth.
 * After save, the local state is cleared anyway (see successSaveDashConfig).
 * @param {boolean} isAfterSave - Whether this is called after a save
 * @param {object} state - Current Redux state
 * @param {string} serialNumber - The vehicle serial number (unused, kept for API compatibility)
 * @returns {object} Object with removedComponents and savedAddedComponents
 */
const getPendingChangesForConfig = (isAfterSave, state) => {
  // On page load/refresh: start fresh. The API's pendingEdexUpdate flag
  // is the single source of truth for pending changes.
  // Local tracking (removedComponents, savedAddedComponents) is only for unsaved changes.
  return {
    removedComponents: {},
    savedAddedComponents: {},
    modificationCount: 0,
  }
}

export const initialState = fromJS({
  switchLinkedLogic: {},
  isDragging: false,
  isSaving: false,
  removeComponents: {},
  componentsWithoutLocation: [],
  locations: {},
  hasApprovedLayout: false,
  hasChanges: false,
  customSwitchGraphics: {},
  switchDetailsChanged: false,
  removeMsf: false,
  removeMsfPanel: null,
  msfModalOpen: false,
  msfModalPanelId: null,
  savedAddedComponents: {}, // Track added components that have been saved (pending programming)
  modificationCount: 0, // Counter that increments on each S&G change for notification
  isLoadingDashConfig: false,
})

const handlers = {
  [actions.SUCCESS_CUSTOM_SWITCH_GRAPHIC]: successCustomSwitchGraphic,
  [actions.FAILURE_CUSTOM_SWITCH_GRAPHIC]: failureCustomSwitchGraphic,
  [actions.SUCCESS_DASH_CONFIG]: successDashConfig,
  [actions.SUCCESS_LAYOUT_BLACKLIST]: successLayoutBlacklist,
  [actions.SUCCESS_SMART_SWITCHES]: successSmartSwitches,
  [actions.RESET_VEHICLE_DASH]: resetVehicleDash,
  [actions.SAVE_DASH_CONFIG]: saveDashConfig,
  [actions.SUCCESS_SAVE_DASH_CONFIG]: successSaveDashConfig,
  [actions.FAILURE_SAVE_DASH_CONFIG]: failureSaveDashConfig,
  [actions.ON_DASH_DRAG]: onDashDrag,
  [actions.OFF_DASH_DRAG]: offDashDrag,
  [actions.ON_DRAG_DROP]: onDragDrop,
  [actions.CLEAR_DRAG_ITEM]: clearDragItem,
  [actions.SWITCH_LINKED_REFERENECE_PARAMETERS_RECIEVED]:
    switchLinkedReferenceParametersRecieved,
  [actions.SWITCH_LINKED_FEATURES_RECIEVED]: switchLinkedFeatureRecieved,
  [actions.REMOVE_COMPONENT]: removeComponent,
  [actions.ADD_SWITCH_TO_LOCATION]: addSwitchToLocation,
  [actions.DISPLAY_ALL_LOCATIONS]: displayAllLocations,
  [actions.DASH_CONFIG_SET_BLOCKED_LIST]: dashConfigSetBlockedList,
  [actions.SUCCESS_SEARCHED_CUSTOM_SWITCHES]: successSearchCustomSwitches,
  [actions.ADD_CUSTOM_GRAPHICS_TO_SWITCH]: addCustomGraphicsToSwitch,
  [actions.SET_SWTICH_DETAILS_CHANGED]: setSwitchDetailsChanged,
  [actions.RESET_SWTICH_DETAILS_CHANGED]: resetSwitchDetailsChanged,
  [actions.SET_UNASSIGNED_LOCATION_DATA]: setUnassignedLocationData,
  [actions.RESET_SELECTED_LOCATION_TYPE]: resetSelectedLocationType,
  [actions.ADD_MSF]: addMsf,
  [actions.ADD_MSF_TO_LOCATION]: addMsfToLocation,
  [actions.REMOVE_MSF]: removeMsfHandler,
  [actions.REMOVE_MSF_FROM_LOCATION]: removeMsfFromLocationHandler,
  [actions.SET_MSF_MODAL_OPEN]: setMsfModalOpenHandler,
  [actions.ADD_COMPONENT_TO_STAGING]: addComponentToStaging,
  [actions.SET_LAYOUT_LOCATION_TO_UPDATE]: setLayoutLocationToUpdate,
  [actions.UPDATE_LOCATION]: updateLocation,
  [actions.RESET_LOCATION]: resetLocation,
  [actions.SET_HAS_APPROVED_LAYOUT]: setHasApprovedLayout,
  [actions.SUCCESS_SAVE_LAYOUT_LOCATION]: successSaveLayoutLocation,
  [actions.SAVE_LAYOUT_LOCATION]: saveLayoutLocation,
  // NOTE: REMOVE_SNG_PENDING_NOTIFICATION should NOT reset the counter
  // The counter should only reset when changes are programmed to DiagnosticLink
  // which happens when the vehicle is reloaded with fresh data from the server
  [GET_TRUCK_DETAILS]: clearPendingStateOnVehicleChange,
  [actions.REQUESTING_DASH_CONFIG]: requestingDashConfig,
  [actions.FAILURE_DASH_CONFIG]: failureDashConfig,
}

/**
 * Clear pending S&G state when navigating to a different vehicle.
 * This prevents stale notifications from the previous vehicle showing briefly
 * before the new vehicle's data loads.
 */
function clearPendingStateOnVehicleChange(state, action) {
  const currentSerial = state.get('serialNumber')
  const newSerial = action.serialNumber

  // Only clear if navigating to a DIFFERENT vehicle
  // (Don't clear on F5 refresh of same vehicle)
  if (
    currentSerial &&
    newSerial &&
    currentSerial.toUpperCase() !== newSerial.toUpperCase()
  ) {
    return state
      .set('removedComponents', Map())
      .set('savedAddedComponents', Map())
      .set('modificationCount', 0)
      .set('meta', Map())
      .set('components', Map())
      .set('locations', Map())
      .set('componentsWithoutLocation', List())
  }

  return state
}

function requestingDashConfig(state) {
  return state.set('isLoadingDashConfig', true)
}

function failureDashConfig(state) {
  return state.set('isLoadingDashConfig', false)
}

export default createReducer(initialState, handlers)

/**
 * A flag for the async action of saving the layout location.
 * @param {object} state
 * @returns {object}
 */
function saveLayoutLocation(state) {
  return state.set('isSavingLayoutLocation', true)
}

/**
 * When a layout save is successfull, the whole dash is not refreshed.
 * This means that all the original dash layout info and the working info needs to be updated.
 * @param {boolean} state
 * @param {DashLayoutLocation} data
 * @returns {object}
 */
function successSaveLayoutLocation(state, { data }) {
  const name = data?.name

  if (name) {
    const layouts = state.getIn(['originalDash', 'layouts']).map((layout) => {
      if (layout.get('name') === name) return layout.merge(fromJS(data))
      return layout
    })

    return state
      .set('isSavingLayoutLocation', false)
      .setIn(['originalDash', 'locationsAsMap', name], fromJS(data))
      .setIn(['originalDash', 'layouts'], layouts)
      .set('layoutLocationUpdate', Map())
  }

  return state
}

/**
 * A reducer to set the has approved layout flag.
 * @param {object} state
 * @param {bool} hasApproved
 * @returns {object}
 */
function setHasApprovedLayout(state, { hasApproved }) {
  return state.set('hasApprovedLayout', hasApproved)
}

/**
 * A reducer used to reset a dash layout location from the original dash layout location value.
 * @param {object} state
 * @param {{ location: string }} action
 * @returns {object}
 */
function resetLocation(state, { location }) {
  const originalLocation = getIn(
    ['originalDash', 'locationsAsMap', location],
    state
  )

  const updatedLocationNames = getOr(List(), 'updatedLocationNames', state)

  return state.setIn(['locations', location], originalLocation).set(
    'updatedLocationNames',
    updatedLocationNames.filter((loc) => loc !== location)
  )
}

/**
 * A reducer to update the values of a layout location.
 *
 * @param {object} state
 * @param {{ location: DashLayoutLocation }} param1
 * @returns {object}
 */
function updateLocation(state, { location }) {
  const { name } = location
  const isSavingLayoutLocation = state.get('isSavingLayoutLocation')

  if (isSavingLayoutLocation) return state

  const updatedLocations = getOr(List(), 'updatedLocationNames', state)
  const nextUpdatedLocations = updatedLocations.push(name)
  const nextUpdatedLocationsUnique = nextUpdatedLocations.toSet().toList()

  return state
    .setIn(['locations', name], fromJS(location))
    .set('updatedLocationNames', nextUpdatedLocationsUnique)
}

/**
 * A reducer to set a temporary capture of the layout location data that is being edited.
 *
 * @param {object} state
 * @param {{ location: DashLayoutLocation }} param1
 * @returns {object}
 */
function setLayoutLocationToUpdate(state, { location }) {
  return state.set('layoutLocationToUpdate', location)
}

function dashConfigSetBlockedList(state, { blockedList }) {
  const vehicleSubType = state.get('vehicleSubType')

  if (CHEC_PLUS_VEHICLE_SUBTYPES.includes(vehicleSubType)) {
    // these locations have to be removed from the blocked list to allow Pocket and MSF swapping
    // they are in the blocked list because engineering need to block S&G builder from putting a pocket on an MSF, etc
    const locationsToRemove = [
      'LSMAN',
      'RSMAN',
      'LPMN1',
      'LPMN2',
      'LPCT1',
      'LPCT2',
      'LSPCT',
      'RSPCT',
    ]
    const list = blockedList.filter((loc) => !locationsToRemove.includes(loc))
    return set('blockedList', list, state)
  }

  return set('blockedList', blockedList, state)
}

function successSearchCustomSwitches(state, { searchedString, data }) {
  const transform = compose(
    set('searchedCustomSwitchParts', data),
    set('searchedCustomSwitchPartString', searchedString),
    set(
      'selectedCustomGraphicsToSwitch',
      getOr(Map(), 'selectedCustomGraphicsToSwitch', state)
    )
  )
  return transform(state)
}

function setSwitchDetailsChanged(state) {
  const selectedGraphicsByPanelNumber = state.get(
    'selectedCustomGraphicsToSwitch'
  )
  const components = state.get('components')

  const nextComponents = components.map((component) => {
    const panelNumber = component.get('panelNumber')
    if (
      selectedGraphicsByPanelNumber &&
      selectedGraphicsByPanelNumber.has(panelNumber)
    ) {
      const capPartNumber = getIn(
        [panelNumber, 'partNumber'],
        selectedGraphicsByPanelNumber
      )

      let newComponent = Map(component)
      if (!newComponent.has('removedCapPartNumber')) {
        newComponent = newComponent.set(
          'removedCapPartNumber',
          component.get('capPartNumber')
        )
      }
      newComponent = newComponent.set('capPartNumber', capPartNumber)
      return newComponent
    }
    return component
  }, List())

  return state
    .set('switchDetailsChanged', true)
    .set('components', nextComponents)
}

function setUnassignedLocationData(
  state,
  { location, locationDetail, locationType }
) {
  const transform = compose(
    setIn(['unassignedComponents', location, 'locationType'], locationType),
    setIn(['unassignedComponents', location, 'locationDetail'], locationDetail)
  )
  return updateComponentMapsFromState(transform(state))
}

function resetSwitchDetailsChanged(state) {
  const transform = compose(
    set('searchedCustomSwitchParts', null),
    set('searchedCustomSwitchPartString', ''),
    set('selectedCustomGraphicsToSwitch', Map()),
    set('switchDetailsChanged', false)
  )
  return transform(state)
}

function addCustomGraphicsToSwitch(
  state,
  { partNumber, description, panelNumber }
) {
  return setIn(
    ['selectedCustomGraphicsToSwitch', panelNumber],
    fromJS({ partNumber, description, panelNumber }),
    state
  )
}

function resetSelectedLocationType(state, { location }) {
  const transform = compose(
    setIn(['unassignedComponents', location, 'locationType'], null),
    setIn(['unassignedComponents', location, 'locationDetail'], null)
  )
  return transform(state)
}

function displayAllLocations(state, { displayAllLocations }) {
  return set('displayAllLocations', displayAllLocations, state)
}

function addMsf(state, { addMsf, id }) {
  const transform = compose(set('addMsf', addMsf), set('addMsfPanel', id))
  return transform(state)
}

function addMsfToLocation(state, { location }) {
  const vehicleSubType = safeToJS(get('vehicleSubType', state))
  const dbCodes = get('dataBookCodes', state)

  const new43NMsf = {
    panelNumber: 'A66-07492-001',
    panelDescription: 'MDL-MSF,4 SW RCPT,CHRM,WST',
  }

  const new42NMsf = {
    panelNumber: 'A06-90732-005',
    panelDescription: 'MDL-MSF,4 SW RCPT,CHRM,M2/P4+',
  }

  const newP4wMsf = {
    panelNumber: 'A06-90732-003',
    panelDescription: 'MDL-MSF,4 SW RCPT,CHRM,P4W',
  }

  const selectMsfType = () => {
    const isRightHandDrive = dbCodes.includes(RHD_43N_38N_CODE)

    if (isRightHandDrive) return newP4wMsf

    return vehicleSubType === TRUCK_SUBTYPE_42N ? new42NMsf : new43NMsf
  }

  const msfLayoutDetail = getIn(['locations', location, 'detail'], state)
  const newMsf = {
    location,
    type: HARDWIRED_COMPONENT,
    bom: '',
    hasBeenAdded: true,
    pendingEdexUpdate: false,
    isCustom: false,
    isPlug: false,
    ...selectMsfType(),
    locationDetail: msfLayoutDetail,
    locationType: 'MSF',
  }

  const { components, removedComponents, blackList } = pickToJS(
    ['components', 'removedComponents', 'blackList'],
    state
  )

  const newComponentsLists = {
    ...components,
    [location]: newMsf,
  }
  let newRemovedComponentList = {
    ...removedComponents,
  }

  // If this location was previously removed, clean it from removedComponents
  // since adding a new MSF here effectively cancels the removal
  delete newRemovedComponentList[location]

  const locationType = getIn(['locations', location, 'type'], state)

  // check if pocket has component then remove the pocket
  if (locationType === POCKET && has(location, get('components', state))) {
    const pocketComponent = prop(location, components)
    newRemovedComponentList = {
      ...newRemovedComponentList,
      [location]: pocketComponent,
    }
  }

  // check for MSF location if it has dependant pocket then remove pocket
  if (Object.keys(MSF_POCKET_LOCATIONS_MAP).includes(location)) {
    const dependantPocketLocation = MSF_POCKET_LOCATIONS_MAP[location]
    if (has(dependantPocketLocation, get('components', state))) {
      const pocketComponent = getIn(
        ['components', dependantPocketLocation],
        state
      )
      delete newComponentsLists[dependantPocketLocation]
      newRemovedComponentList = {
        ...newRemovedComponentList,
        [dependantPocketLocation]: pocketComponent,
      }
    }
  }

  // blocked list has to update when components move around
  const newBlockedList = createBlockedList(
    values(newComponentsLists),
    blackList
  )

  const nextState = {
    ...state.toJS(),
    components: newComponentsLists,
    blockedList: newBlockedList,
    hasChanges: true,
    removedComponents: newRemovedComponentList,
  }

  const layouts = values(state.get('locations').toJS())
  const nextComponents = values(nextState.components).reduce(
    (acc, curr) => ({
      ...acc,
      [curr.location]: {
        ...curr,
        ...createLocationRules(nextState, layouts, curr),
      },
    }),
    {}
  )

  return updateComponentMapsFromState(
    fromJS({
      ...nextState,
      components: nextComponents,
      modificationCount: (state.get('modificationCount') || 0) + 1,
    })
  )
}

function removeMsfHandler(state, { removeMsf, id }) {
  const transform = compose(
    set('removeMsf', removeMsf),
    set('removeMsfPanel', id)
  )
  return transform(state)
}

function setMsfModalOpenHandler(
  state,
  { isOpen, panelId, enableAddMsf, disableAddMsfReason }
) {
  const transform = compose(
    set('msfModalOpen', isOpen),
    set('msfModalPanelId', panelId),
    set('msfModalEnableAddMsf', Boolean(enableAddMsf)),
    set('msfModalDisableAddMsfReason', disableAddMsfReason || null)
  )
  return transform(state)
}

function removeMsfFromLocationHandler(state, { location }) {
  const { components, removedComponents, locations, blackList } = pickToJS(
    ['components', 'removedComponents', 'locations', 'blackList'],
    state
  )

  const msfComponent = components[location]
  if (!msfComponent) return state

  const newComponents = { ...components }
  const newRemovedComponents = { ...removedComponents }

  // Remove the MSF component - only track as "removed" if it wasn't added this session
  if (!msfComponent.hasBeenAdded) {
    newRemovedComponents[location] = msfComponent
  }
  delete newComponents[location]

  // Remove all child switch components from the MSF
  const msfChildren = propOr([], 'children', locations[location])
  msfChildren.forEach((childLocation) => {
    if (newComponents[childLocation]) {
      newRemovedComponents[childLocation] = newComponents[childLocation]
      delete newComponents[childLocation]
    }
  })

  const newBlockedList = createBlockedList(values(newComponents), blackList)

  const nextState = {
    ...state.toJS(),
    components: newComponents,
    removedComponents: newRemovedComponents,
    blockedList: newBlockedList,
    hasChanges: true,
    removeMsf: false,
    removeMsfPanel: null,
    msfModalOpen: false,
    msfModalPanelId: null,
  }

  const layouts = values(state.get('locations').toJS())
  const nextComponents = values(nextState.components).reduce(
    (acc, curr) => ({
      ...acc,
      [curr.location]: {
        ...curr,
        ...createLocationRules(nextState, layouts, curr),
      },
    }),
    {}
  )

  return updateComponentMapsFromState(
    fromJS({
      ...nextState,
      components: nextComponents,
      modificationCount: (state.get('modificationCount') || 0) + 1,
    })
  )
}

function successLayoutBlacklist(state, { data }) {
  const blacklists = data.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.location]: curr,
    }),
    {}
  )
  return set('blackList', fromJS(blacklists), state)
}

function successCustomSwitchGraphic(state, { capPartNumber, data }) {
  return setIn(['customSwitchGraphics', capPartNumber], data, state)
}

function failureCustomSwitchGraphic(state, action) {
  const { actionArguments } = action
  const { capPartNumber } = actionArguments

  return setIn(['customSwitchGraphics', capPartNumber], ERROR, state)
}

function successSmartSwitches(state, { data }) {
  const smartSwitches = data.reduce(
    (acc, curr) => ({
      ...acc,
      [prop('panelNumber', curr)]: {
        ...curr,
      },
    }),
    {}
  )

  return set('smartSwitches', fromJS(smartSwitches), state)
}

function saveDashConfig(state) {
  return set('isSaving', true, state)
}

function addComponentToStaging(state, { location }) {
  const {
    components,
    locations,
    stagedComponents,
    blackList,
    stagedLocations,
    stagedMsfCount = 0,
  } = pickToJS(
    [
      'components',
      'locations',
      'stagedComponents',
      'blackList',
      'stagedLocations',
      'stagedMsfCount',
    ],
    state
  )
  let stagedMsfCounter = stagedMsfCount
  const component = components[location]
  const locationDetails = locations[location]
  let newUpdatedComponents = {
    ...components,
  }

  const stagedLocationChildren = propOr([], 'children', locationDetails)
  const locationType = prop('type', locationDetails)

  if (locationType === MSF) {
    stagedMsfCounter += 1
  }

  const shouldStagedChildrenLocations =
    length(stagedLocationChildren) && length(stagedLocationChildren) > 0

  // staged parent component and remove it from component list
  let newStagedComponents = {
    ...stagedComponents,
    [`STAGED_${location}`]: {
      ...component,
      location: `STAGED_${location}`,
      parentPanel: propOr('', 'panel', locationDetails),
      hasChildren: shouldStagedChildrenLocations,
    },
  }
  delete newUpdatedComponents[location]

  let newStagedLocations = {
    ...stagedLocations,
    [`STAGED_${location}`]: {
      ...locationDetails,
      location: `STAGED_${location}`,
      stagedCount: stagedMsfCounter,
    },
  }

  let newLocations = {
    ...locations,
    [`STAGED_${location}`]: {
      ...locationDetails,
      stagedCount: stagedMsfCounter,
    },
  }

  // if parent location has children then staged those children component and remove them from components list
  if (shouldStagedChildrenLocations) {
    const {
      updatedStagedComponents,
      updatedStagedLocations,
      locations: updatedLocations,
      components: updatedComponents,
    } = getNewStagedLocationChildren(
      stagedLocationChildren,
      newLocations,
      newUpdatedComponents
    )

    newLocations = { ...updatedLocations }
    newUpdatedComponents = { ...updatedComponents }

    newStagedComponents = {
      ...newStagedComponents,
      ...updatedStagedComponents,
    }

    newStagedLocations = {
      ...newStagedLocations,
      [`STAGED_${location}`]: {
        ...newStagedLocations[`STAGED_${location}`],
        hasChildren: true,
        children: updatedStagedLocations,
      },
    }

    stagedLocationChildren.forEach((childLocation) => {
      delete newUpdatedComponents[childLocation]
    })

    newStagedLocations[`STAGED_${location}`].children =
      stagedLocationChildren.map((childLocation) => `STAGED_${childLocation}`)
  }

  // blocked list has to update when components move around
  const newBlockedList = createBlockedList(
    values(newUpdatedComponents),
    blackList
  )

  let nextState = {
    ...state.toJS(),
    stagedComponents: newStagedComponents,
    stagedLocations: newStagedLocations,
    blockedList: newBlockedList,
    draggingItem: {},
    components: newUpdatedComponents,
    locations: newLocations,
    displayAllLocations: true,
    hasChanges: true,
    stagedMsfCount,
  }

  const layouts = values(newLocations)
  const componentGroups = [nextState.stagedComponents, nextState.components]
  const [stagedComponentsMap, componentsMap] = componentGroups.map(
    (components) =>
      values(components).reduce(
        (acc, curr) => ({
          ...acc,
          [curr.location]: {
            ...curr,
            ...createLocationRules(nextState, layouts, curr),
          },
        }),
        {}
      )
  )

  nextState = fromJS({
    ...nextState,
    components: componentsMap,
    stagedComponents: stagedComponentsMap,
  })

  return nextState
}

const getNewStagedLocationChildren = (
  childrenLocations,
  locations,
  components
) => {
  const updatedStagedComponents = childrenLocations.reduce((acc, curr) => {
    const updatedComponents = {
      ...acc,
      [`STAGED_${curr}`]: components[curr]
        ? {
            ...components[curr],
            location: `STAGED_${curr}`,
            parentPanel: path([curr, 'panel'], locations),
          }
        : {},
    }

    delete components[curr]

    return updatedComponents
  }, {})

  const updatedStagedLocations = childrenLocations.reduce((acc, curr) => {
    locations[`STAGED_${curr}`] = locations[curr]
      ? {
          ...locations[curr],
          parentPanel: path([curr, 'panel'], locations),
        }
      : undefined
    return {
      ...acc,
      [`STAGED_${curr}`]: locations[curr]
        ? {
            ...locations[curr],
            location: `STAGED_${curr}`,
            parentPanel: path([curr, 'panel'], locations),
          }
        : undefined,
    }
  }, {})

  return {
    updatedStagedComponents,
    updatedStagedLocations,
    locations,
    components,
  }
}

function successSaveDashConfig(state, action) {
  const { data, serialNumber } = action
  // After successful save, the API returns components with pendingEdexUpdate: true
  // This is now the source of truth - clear local tracking state to prevent duplicates
  const newState = set('isSaving', false, state)

  const result = successDashConfig(newState, {
    serialNumber,
    data: data.dashboard,
    isAfterSave: true,
  })

  // Clear local tracking state - API's pendingEdexUpdate is now the source of truth
  // This prevents duplicates in the pending notification (same component in both
  // local removedComponents AND API's pendingEdexUpdate list)
  const clearedResult = result
    .set('removedComponents', Map())
    .set('savedAddedComponents', Map())
    .set('modificationCount', 0)

  return clearedResult
}

function failureSaveDashConfig(state, action) {
  const newState = set('isSaving', false, state)
  return resetVehicleDash(newState, action)
}

const getMainDashDefinition = (
  dashType,
  dbCodes,
  vehicleSubType,
  isPreProduction,
  vehicleType
) =>
  resolveDashDefinition({
    dashboardType: dashType,
    vehicleSubType,
    vehicleType,
    dataBookCodes: dbCodes,
    isPreProduction,
  })

function successDashConfig(state, action) {
  const { serialNumber, data } = action
  const {
    vehicleSubType, // NOTE: For 42N BUS, dashbaord type Q, this subtype will be 42N while the subtype in the /vehicles/{serial} endpoint will say BUS.
    vehicleType,
    dataBookCodes,
    components,
    layouts,
    dashboardType,
    isPreProduction,
    isAllium,
  } = data

  const currentCount = state.get('modificationCount') || 0

  const locationsAsMap = createLayoutsMap(layouts, vehicleSubType)

  const [componentsAsMap, unassignedComponentsAsMap] = createComponentMap(
    {
      ...safeToJS(state),
      locations: locationsAsMap,
      isPreProduction,
      dashboardType,
      dataBookCodes,
      vehicleSubType,
      vehicleType,
      layouts,
      serialNumber,
      isAllium,
    },
    components,
    layouts
  )

  const overheadAndSleeperDefs = resolveOverheadSleeperDefinitions(
    vehicleSubType,
    dataBookCodes
  )

  const mainDashDef = getMainDashDefinition(
    dashboardType,
    dataBookCodes,
    vehicleSubType,
    isPreProduction,
    vehicleType
  )

  const pendingChanges = getPendingChangesForConfig(
    action.isAfterSave,
    state,
    serialNumber
  )

  // CRITICAL: Always use the maximum between current count and pending count
  // This prevents the counter from going backwards
  const finalModificationCount = Math.max(
    currentCount,
    pendingChanges.modificationCount
  )

  const config = fromJS({
    ...omit(['components', 'layouts'], data),
    serialNumber,
    components: componentsAsMap,
    unassignedComponents: unassignedComponentsAsMap,
    locations: locationsAsMap,
    meta: {
      ...(Boolean(length(values(overheadAndSleeperDefs))) &&
        overheadAndSleeperDefs),
      dash: mainDashDef,
    },
    originalDash: {
      ...data,
      componentsAsMap,
      unassignedComponentsAsMap,
      locationsAsMap,
      isPreProduction,
    },
    hasChanges: false,
    // Pending changes state is managed by:
    // - Local tracking (removedComponents, savedAddedComponents) for unsaved changes
    // - API's pendingEdexUpdate flag for saved changes (single source of truth)
    // On page load/refresh, local state is empty - API data is used
    ...pendingChanges,
    stagedLocations: {},
    stagedComponents: {},
  })

  // Merge config with state, but ensure pending changes are preserved
  const merged = merge(config, state)

  // Force set pending change values to prevent them from being overwritten by empty state values
  // The merge takes state values over config values, but we want the restored/calculated values
  return merged
    .set('modificationCount', finalModificationCount)
    .set(
      'savedAddedComponents',
      fromJS(pendingChanges.savedAddedComponents || {})
    )
    .set('removedComponents', fromJS(pendingChanges.removedComponents || {}))
    .set('isLoadingDashConfig', false)
}

function resetVehicleDash(state) {
  // mimic the original post data
  const data = safeToJS(get('originalDash', state))
  delete data.componentsAsMap
  delete data.locationsAsMap
  delete data.unassignedComponentsAsMap

  const serialNumber = get('serialNumber', state)

  const action = {
    serialNumber,
    data,
  }

  /**
   * Rest to inital state plus other API call data.
   * Also need to regenerate blocked list per initial state.
   * /{dashboardType}/blacklist
   * /switches/{vehicleSubType}
   */
  const blackList = safeToJS(state.get('blackList'))
  const blockedList = createBlockedList(values(data.components), blackList)

  const resetState = initialState
    .set('blackList', fromJS(blackList))
    .set('smartSwitches', state.get('smartSwitches'))
    .set('blockedList', fromJS(blockedList))

  return successDashConfig(resetState, action)
}

function switchLinkedFeatureRecieved(
  state,
  { switchId, data: v3, features: v2 }
) {
  // this reducer is used by v3 and v2 endpoints.
  // out of time to bring S&G features/search endpoint into API alignment.
  const features = isArray(v2) ? v2 : prop('features', v3)

  const switchLinkedLogic = setSwitchLinkedLogic(switchId, features)(state)
  return set('switchLinkedLogic', switchLinkedLogic, state)
}

function switchLinkedReferenceParametersRecieved(state, { data, switchId }) {
  const { referenceParameters } = data
  const switchLinkedLogic = setSwitchLinkedLogic(
    switchId,
    referenceParameters
  )(state)
  return set('switchLinkedLogic', switchLinkedLogic, state)
}

function onDashDrag(state, { draggingItem }) {
  const immDraggingItem = fromJS(draggingItem)
  const transform = compose(
    set('isDragging', true),
    set('draggingItem', immDraggingItem)
  )
  return transform(state)
}

function offDashDrag(state) {
  const transform = compose(
    set('isDragging', false),
    set('draggingItem', Map())
  )
  return transform(state)
}

function clearDragItem(state) {
  return set('draggingItem', Map(), state)
}

// ToDo: update to use immutable
function onDragDrop(state, { dropItem }) {
  const { toLocation, fromLocation } = dropItem

  // early exit if same location
  if (toLocation === fromLocation) return state

  const {
    components,
    unassignedComponents,
    locations,
    blackList,
    stagedComponents,
    stagedLocations,
  } = pickToJS(
    [
      'components',
      'unassignedComponents',
      'locations',
      'blackList',
      'stagedComponents',
      'stagedLocations',
    ],
    state
  )

  // handle POC and MSF swaps before others
  const fromLocationType = path([fromLocation, 'type'], locations)
  const toLocationType = path([toLocation, 'type'], locations)
  const fromLocationPanel = path([fromLocation, 'panel'], locations)
  const toLocationPanel = path([toLocation, 'panel'], locations)
  const typesSorted = [fromLocationType, toLocationType].sort()
  const isSwappingMsfAndPocket =
    typesSorted[0] === 'MSF' && typesSorted[1].includes('POC')
  const isFromLocationIsOnAGPanel = ['AGFAC', 'AGPNL'].includes(
    fromLocationPanel
  )
  const isToLocationIsOnAGPanel = ['AGFAC', 'AGPNL'].includes(toLocationPanel)

  // SWAPPING MSF AND POCKET
  // RETURNS NEW STATE
  if (
    isSwappingMsfAndPocket &&
    isFromLocationIsOnAGPanel &&
    isToLocationIsOnAGPanel
  ) {
    // swamp location information and location Ids
    const fromComponent = prop(fromLocation, components)
    const toComponent = prop(toLocation, components)

    const newFromComponentLocation = MSF_POCKET_LOCATIONS_MAP[fromLocation]
    const newFromComponent = {
      ...toComponent,
      location: newFromComponentLocation,
    }

    const newToComponentLocation = MSF_POCKET_LOCATIONS_MAP[toLocation]
    const newToComponent = {
      ...fromComponent,
      location: newToComponentLocation,
    }

    // update children components
    locations[newFromComponentLocation].children.forEach((location, index) => {
      const toChildLocation = path([toLocation, 'children', index], locations)
      const component = components[toChildLocation]

      if (component) {
        delete components[component.location]
        component.location = location
        components[location] = component
      }
    })

    locations[newToComponentLocation].children.forEach((location, index) => {
      const fromChildLocation = path(
        [fromLocation, 'children', index],
        locations
      )
      const component = components[fromChildLocation]

      if (component) {
        delete components[component.location]
        component.location = location
        components[location] = component
      }
    })

    // remove old locations
    delete components[fromLocation]
    delete components[toLocation]

    // update components
    const newComponents = {
      ...components,
      [newFromComponentLocation]: newFromComponent,
      [newToComponentLocation]: newToComponent,
    }

    // remove undefined locations
    keys(newComponents).forEach((key) => {
      if (newComponents[key] === undefined) delete newComponents[key]
    })

    // blocked list has to update when components move around
    const newBlockedList = createBlockedList(values(newComponents), blackList)

    const transform = compose(
      set('components', fromJS(newComponents)),
      set('hasChanges', true),
      set('blockedList', fromJS(newBlockedList))
    )

    const nextState = updateComponentMapsFromState(transform(state))

    return nextState
  }

  // handle all other swap/ondrop starting here
  const isFromUnassigned = Object.hasOwn(unassignedComponents, fromLocation)
  const isFromStaged = isStagedComponent(fromLocation)

  const newFromLocation = getNewFromLocation(
    components,
    toLocation,
    fromLocation
  )
  const newToLocation = getNewToLocation(
    isFromUnassigned,
    unassignedComponents,
    components,
    toLocation,
    fromLocation,
    isFromStaged,
    stagedComponents
  )

  let newComponentsLists = {
    ...components,
    [fromLocation]: newFromLocation,
    [toLocation]: newToLocation,
  }

  let newStagedLocations = { ...stagedLocations }
  let newStagedComponents = { ...stagedComponents }
  let newLocations = { ...locations }
  const toLocationChildren = path([toLocation, 'children'], locations)

  // SWAPPING FROM STAGE
  if (isFromStaged) {
    delete newComponentsLists[fromLocation]

    const hasChildren = path([fromLocation, 'hasChildren'], stagedLocations)

    // if staged component is MSF then we need to move all the children to new location as well
    if (hasChildren) {
      const fromLocations = path([fromLocation, 'children'], stagedLocations)
      const isComponentExist = propOr(false, toLocation, components)

      if (isComponentExist) {
        const { updatedToLocationChildren, updatedFromLocationChildren } =
          getNewToStagedLocationChildren(
            fromLocations,
            toLocationChildren,
            newStagedComponents,
            newComponentsLists
          )

        newStagedComponents = {
          ...newStagedComponents,
          ...updatedFromLocationChildren,
        }
        newComponentsLists = {
          ...newComponentsLists,
          ...updatedToLocationChildren,
        }
      } else {
        const {
          toLocationChildrenComponents,
          updatedStagedLocations,
          updatedStagedComponents,
          updatedLocations,
        } = getNewStagedChildrenLocation(
          fromLocations,
          toLocationChildren,
          newStagedLocations,
          newStagedComponents,
          locations
        )

        newStagedLocations = {
          ...newStagedLocations,
          ...updatedStagedLocations,
        }
        newStagedComponents = {
          ...newStagedComponents,
          ...updatedStagedComponents,
        }
        newLocations = { ...newLocations, ...updatedLocations }

        newComponentsLists = {
          ...newComponentsLists,
          ...toLocationChildrenComponents,
        }

        delete newLocations[fromLocation]
        delete newStagedLocations[fromLocation]
        delete newStagedComponents[fromLocation]
      }
    } else {
      const newStagedComponent = getNewUnassignedFromLocation(
        toLocation,
        fromLocation,
        components
      )
      if (newStagedComponent) {
        newStagedComponents[fromLocation] = newStagedComponent
      } else {
        delete newLocations[fromLocation]
        delete newStagedLocations[fromLocation]
        delete newStagedComponents[fromLocation]
      }
    }
  }

  // SWAPPING FROM UNASSIGNED
  if (isFromUnassigned) {
    delete newComponentsLists[fromLocation]
    const newUnassignedComponent = getNewUnassignedFromLocation(
      toLocation,
      fromLocation,
      components
    )
    const isPlug = newUnassignedComponent
      ? newUnassignedComponent.isPlug
      : false
    if (!isPlug) {
      unassignedComponents[fromLocation] = newUnassignedComponent
    } else {
      delete unassignedComponents[fromLocation]
    }
  }

  // find out if fromLocation has children, if has children, move all children to toLocation children
  const fromLocationChildren = path([fromLocation, 'children'], locations)

  const shouldManageLocationChildren = getShouldManageLocationChildren(
    fromLocationChildren,
    toLocationChildren
  )

  // ToDo: This should be a recursive update, only changing direct children right now
  // Though as of now we only have MSF/Switch parent-child that needs this. and it is not staged components
  if (shouldManageLocationChildren && !isFromStaged) {
    const updatedFromLocationChildren = getNewFromLocationChildren(
      fromLocationChildren,
      toLocationChildren,
      components
    )
    const updatedToLocationChildren = getNewToLocationChildren(
      toLocationChildren,
      fromLocationChildren,
      components
    )

    newComponentsLists = {
      ...newComponentsLists,
      ...updatedFromLocationChildren,
      ...updatedToLocationChildren,
    }

    // Mark swapped parent components for pending display
    if (newComponentsLists[fromLocation]) {
      newComponentsLists[fromLocation] = {
        ...newComponentsLists[fromLocation],
        hasBeenSwapped: true,
      }
    }
    if (newComponentsLists[toLocation]) {
      newComponentsLists[toLocation] = {
        ...newComponentsLists[toLocation],
        hasBeenSwapped: true,
      }
    }
  }

  // remove undefined locations
  keys(newComponentsLists).forEach((key) => {
    if (newComponentsLists[key] === undefined) delete newComponentsLists[key]
  })

  // remove undefined locations
  keys(unassignedComponents).forEach((key) => {
    if (unassignedComponents[key] === undefined)
      delete unassignedComponents[key]
  })

  // remove undefined locations
  keys(newStagedComponents).forEach((key) => {
    if (newStagedComponents[key] === undefined) delete newStagedComponents[key]
  })

  // blocked list has to update when components move around
  const newBlockedList = createBlockedList(
    values(newComponentsLists),
    blackList
  )

  const transform = compose(
    set('unassignedComponents', fromJS(unassignedComponents)),
    set('blockedList', fromJS(newBlockedList)),
    set('draggingItem', Map()),
    set('components', fromJS(newComponentsLists)),
    set('locations', fromJS(newLocations)),
    set('stagedLocations', fromJS(newStagedLocations)),
    set('stagedComponents', fromJS(newStagedComponents)),
    set('hasChanges', true),
    set('modificationCount', (state.get('modificationCount') || 0) + 1),
    set(
      'removedComponents',
      updateRemovedComponentsListIfToLocationIsPlug(
        toLocation,
        isFromUnassigned,
        state
      )
    )
  )

  return updateComponentMapsFromState(transform(state))
}

function removeComponent(state, { location }) {
  const isFromUnassigned = isUnassignedComponent(location)
  const componentFrom = isFromUnassigned ? 'unassignedComponents' : 'components'
  const component = getIn([componentFrom, location], state)
  const currentCount = state.get('modificationCount') || 0
  const newCount = currentCount + 1
  const transform = compose(
    set('hasChanges', true),
    set('modificationCount', newCount),
    setIn(['removedComponents', location], component),
    removeIn(['components', location]),
    removeIn(['unassignedComponents', location])
  )
  const newState = transform(state)

  return newState
}

function addSwitchToLocation(state, { panelNumber, location }) {
  const smartSwitch = getSmartSwitchToAdd(panelNumber, location)(state)
  const newCount = (state.get('modificationCount') || 0) + 1

  // Track the existing switch as removed (Doc 12 - PBI 147363)
  const existingComponent = getIn(['components', location], state)
  const shouldTrackRemoval =
    existingComponent && !existingComponent.get('hasBeenAdded')

  // If found in the smart switches catalog, use existing flow
  if (smartSwitch && smartSwitch.get && smartSwitch.get('panelNumber')) {
    // Detect same-signal swap (Doc 13 - PBI 147363)
    const isSameSignalSwap =
      shouldTrackRemoval &&
      existingComponent.get('signalName') &&
      existingComponent.get('signalName') === smartSwitch.get('signalName')

    // If same-signal swap, mark both components so notification is suppressed
    const removedEntry = isSameSignalSwap
      ? existingComponent.set('isSwapReplacement', true)
      : existingComponent
    const updatedRemovedComponents = shouldTrackRemoval
      ? setIn([location], removedEntry, get('removedComponents', state))
      : get('removedComponents', state)

    const smartSwitchToPlace = isSameSignalSwap
      ? smartSwitch.set('isSwapReplacement', true)
      : smartSwitch
    const newComponents = updateComponents(location, smartSwitchToPlace)(state)

    // Also remove any matching unassigned component to prevent duplicates
    // (e.g., when a feature added the switch to unassigned and user is now placing it)
    const unassigned = safeToJS(state.get('unassignedComponents'))
    const matchingUnassignedKey = Object.entries(unassigned).find(
      ([, comp]) => comp.panelNumber === panelNumber
    )?.[0]

    const transform = matchingUnassignedKey
      ? compose(
          set('components', newComponents),
          set(
            'unassignedComponents',
            state.get('unassignedComponents').delete(matchingUnassignedKey)
          ),
          set('hasChanges', true),
          set('modificationCount', newCount),
          set('removedComponents', updatedRemovedComponents)
        )
      : compose(
          set('components', newComponents),
          set('hasChanges', true),
          set('modificationCount', newCount),
          set('removedComponents', updatedRemovedComponents)
        )

    const newState = updateComponentMapsFromState(transform(state))
    return newState
  }

  // Fallback: find in unassigned components by panelNumber
  const unassigned = safeToJS(state.get('unassignedComponents'))
  const unassignedEntry = Object.entries(unassigned).find(
    ([, comp]) => comp.panelNumber === panelNumber
  )

  if (unassignedEntry) {
    const [unassignedKey, unassignedComp] = unassignedEntry
    const componentToAdd = fromJS({
      ...unassignedComp,
      location,
      hasBeenAdded: true,
    })
    const newComponents = set(location, componentToAdd, state.get('components'))
    const newUnassigned = state
      .get('unassignedComponents')
      .delete(unassignedKey)

    const transform = compose(
      set('components', newComponents),
      set('unassignedComponents', newUnassigned),
      set('hasChanges', true),
      set('modificationCount', newCount),
      set(
        'removedComponents',
        updateRemovedComponentsListIfToLocationIsPlug(location, false, state)
      )
    )

    const newState = updateComponentMapsFromState(transform(state))
    return newState
  }

  return state
}

/** Helpers */

const updateComponents = (location, component) =>
  compose(set(location, component), get('components'))

const getSmartSwitchToAdd = (panelNumber, location) =>
  compose(
    set('hasBeenAdded', true),
    set('location', location),
    getIn(['smartSwitches', panelNumber])
  )

const createLayoutsMap = (layouts = []) => {
  // this can be removed and just ust layouts.reduce once
  // children key is removed from API
  const resetChildren = layouts.map(assoc('children', []))

  const locations = resetChildren.reduce((acc, curr) => {
    const name = prop('name', curr)

    return {
      ...acc,
      [name]: curr,
    }
  }, {})

  // mutation, rebuild location children
  values(locations).forEach((location) => {
    const parentId = location.parent
    const parent = locations[parentId]

    if (parent) {
      const locationId = prop('name', location)
      const children = propOr([], 'children', parent)
      locations[parentId].children = [...children, locationId]
    }
  })

  return locations
}

/** PBI 556076: Create rules safely when a component has no panel number. */
const createLocationRules = (state, layouts, component) => {
  const { vehicleSubType, dashboardType, dataBookCodes, components } = state
  const isBus = isDashTypeBus(vehicleSubType, dashboardType, dataBookCodes)

  const isUnassignedTBBSwitchComponent =
    component.location.startsWith('UNASSIGNED_PANEL') &&
    component.locationType === LOCATION_TYPE_TBB_SWITCH

  const isUnassignedTBBOrSmartSwitchType =
    isUnassignedTBBSwitchComponent ||
    (component.type === SMART_SWITCH && !isBus)

  const locationRules = layouts.reduce(
    (layoutsAcc, location) => {
      const result = allowedLocationsRulesEngine(state)(component)(location)

      const isLocationInsideTemOrAuPanel =
        [LOCATION_TYPE_TBB_SWITCH, LOCATION_TYPE_SINGLE_SWITCH_MSF].includes(
          location.type
        ) &&
        (location.parent === TEM_PANEL_LOCATION_ID ||
          location.parent === AUX_PANEL_LOCATION_ID)

      const isTBBSwitchAllowedInTEMOrAUPanel =
        component.panelNumber?.startsWith(TBB) &&
        isBus &&
        isLocationInsideTemOrAuPanel &&
        (!components[location?.name] || components[location?.name]?.isPlug)

      const isSmartSwitchAllowedInTEMOrAUPanel =
        component.type === SMART_SWITCH &&
        !isBus &&
        isLocationInsideTemOrAuPanel &&
        (!components[location?.name] || components[location?.name]?.isPlug)

      if (
        isUnassignedTBBOrSmartSwitchType &&
        (isTBBSwitchAllowedInTEMOrAUPanel || isSmartSwitchAllowedInTEMOrAUPanel)
      ) {
        return {
          ...layoutsAcc,
          allowed: [...layoutsAcc.allowed, location.name],
        }
      }

      if (result.allowed && !isUnassignedTBBSwitchComponent) {
        return {
          ...layoutsAcc,
          allowed: [...layoutsAcc.allowed, location.name],
        }
      }

      return {
        ...layoutsAcc,
        disallowed: [
          ...layoutsAcc.disallowed,
          {
            location: location.name,
            rule: result.rule,
          },
        ],
      }
    },
    {
      allowed: [],
      disallowed: [],
    }
  )

  return locationRules
}

/**
 * Create the components maps from existing state.
 * @param {{Immutable}} state
 * @returns {{Immutable}} state
 */
const updateComponentMapsFromState = (state) => {
  const pojoState = safeToJS(state)
  const components = Object.values(pojoState.components)
  const unassignedComponents = Object.values(pojoState.unassignedComponents)
  const layouts = Object.values(pojoState.locations)

  const [componentsMap, unassignedComponentsMap] = createComponentMap(
    pojoState,
    [...unassignedComponents, ...components],
    layouts
  )

  const nextState = {
    ...pojoState,
    components: componentsMap,
    unassignedComponents: unassignedComponentsMap,
  }

  return fromJS(nextState)
}

/**
 * This is a POJO function.
 * Creates the components and unassigned components maps and allowed locations.
 * PBI 556076: Map components safely when panel numbers are absent.
 *
 * @param {{
 *  layouts: object[],
 *  locations: object,
 *  dashboardType: string,
 *  isPreProduction: boolean,
 *  dataBookCodes: string[],
 * }} state
 * @param {object[]} components
 * @param {object[]} layouts
 * @returns
 */
const createComponentMap = (state, components, layouts) => {
  const isAllium = state.isAllium ?? false
  const getIsUnknownLocation = (location) => {
    if (!isAllium || !isString(location) || location === '') {
      return false
    }
    const allLocations = layouts.map((layout) => layout.name)
    return !allLocations.includes(location)
  }

  const { assigned, unassigned } = components.reduce(
    (acc, component) => {
      const isUnknownLocation = getIsUnknownLocation(component.location)
      // is unassigned location
      if (
        !component?.location ||
        component?.location.includes(UNASSIGNED_PANEL_PART) ||
        isUnknownLocation
      ) {
        // make some data not supplied by API but rules engine needs
        const key = component?.location || makeRandId(UNASSIGNED_PANEL_PART)
        let locationDetail =
          component.type === SMART_SWITCH
            ? SWITCH_DETAIL_BY_MODEL_CODE[state.dashboardType]
            : component.locationDetail

        let locationType =
          component.type === SMART_SWITCH ? SWITCH : component.locationType

        if (component.panelNumber?.startsWith(TBB)) {
          locationDetail = SWITCH_DETAIL_BY_MODEL_CODE.TBB
          locationType = !component.locationType
            ? LOCATION_TYPE_TBB_SWITCH
            : locationType
        }

        // make the new unassigned component
        const unassignedComponent = {
          ...component,
          location: key,
          locationDetail,
          locationType,
          errorCode: component.errorCode || '01',
        }

        return {
          ...acc,
          unassigned: {
            ...acc.unassigned,
            [key]: unassignedComponent,
          },
        }
      }

      const { location } = component

      return {
        ...acc,
        assigned: {
          ...acc.assigned,
          [location]: component,
        },
      }
    },
    {
      assigned: {},
      unassigned: {},
    }
  )

  const newState = {
    ...state,
    components: assigned,
    unassignedComponents: unassigned,
  }

  const unassignedComponents = Object.values(unassigned).reduce((acc, curr) => {
    const rules = createLocationRules(newState, layouts, curr)

    return {
      ...acc,
      [curr.location]: {
        ...curr,
        ...rules,
      },
    }
  }, {})

  const unassignedSmartSwitchLocations = Object.values(
    unassignedComponents
  ).reduce((acc, curr) => {
    const { location, type } = curr
    if (type === SMART_SWITCH) return [...acc, location]
    return acc
  }, [])

  const assignedComponents = Object.values(assigned).reduce((acc, curr) => {
    const rules = createLocationRules(newState, layouts, curr)

    // Shim in the unassigned smart-switch locations for Plugs.
    // Todo: This is not a good idea, location rules should be set after all locations are set.
    // Unassigned location should just be merged into locations.
    if (
      curr.panelDescription.toUpperCase().includes(PLUG) ||
      curr.locationType === LOCATION_TYPE_TBB_SWITCH
    ) {
      rules.allowed = [...rules.allowed, ...unassignedSmartSwitchLocations]
    }

    return {
      ...acc,
      [curr.location]: {
        ...curr,
        ...rules,
      },
    }
  }, {})

  return [assignedComponents, unassignedComponents]
}

const setSwitchLinkedLogic = (switchId, resource) =>
  compose(set(switchId, fromJS(resource)), get('switchLinkedLogic'))

const getNewToLocation = (
  isFromUnassigned,
  unassignedComponents,
  components,
  toLocation,
  fromLocation,
  isFromStaged,
  stagedComponents
) => {
  if (isFromUnassigned) {
    return {
      ...unassignedComponents[fromLocation],
      location: toLocation,
    }
  }

  if (isFromStaged) {
    return {
      ...stagedComponents[fromLocation],
      location: toLocation,
    }
  }

  return {
    ...components[fromLocation],
    location: toLocation,
  }
}

const getNewFromLocation = (components, toLocation, fromLocation) =>
  components[toLocation]
    ? {
        ...components[toLocation],
        location: fromLocation,
      }
    : undefined

const getShouldManageLocationChildren = (
  fromLocationChildren,
  toLocationChildren
) =>
  length(fromLocationChildren) &&
  length(fromLocationChildren) === length(toLocationChildren)

const getNewFromLocationChildren = (
  fromLocationChildren,
  toLocationChildren,
  components
) =>
  fromLocationChildren.reduce(
    (acc, curr, index) => ({
      ...acc,
      [toLocationChildren[index]]: components[fromLocationChildren[index]]
        ? {
            ...components[curr],
            location: toLocationChildren[index],
          }
        : undefined,
    }),
    {}
  )

const getNewToStagedLocationChildren = (
  fromLocationChildren,
  toLocationChildren,
  newStagedComponents,
  components
) => {
  const updatedToLocationChildren = fromLocationChildren.reduce(
    (acc, curr, index) => {
      const updatedComponents = {
        ...acc,
        [toLocationChildren[index]]: newStagedComponents[
          fromLocationChildren[index]
        ]
          ? {
              ...newStagedComponents[curr],
              location: toLocationChildren[index],
            }
          : undefined,
      }

      return updatedComponents
    },
    {}
  )

  const updatedFromLocationChildren = toLocationChildren.reduce(
    (acc, curr, index) => {
      const updatedComponents = {
        ...acc,
        [fromLocationChildren[index]]: components[curr]
          ? {
              ...components[curr],
              location: fromLocationChildren[index],
            }
          : undefined,
      }

      return updatedComponents
    },
    {}
  )

  return {
    updatedToLocationChildren,
    updatedFromLocationChildren,
  }
}

const getNewStagedChildrenLocation = (
  fromLocations,
  toLocationChildren,
  stagedLocations,
  stagedComponents,
  locations
) => {
  const updatedToLocationChildren = fromLocations.reduce((acc, curr, index) => {
    // once staged component is moved to new location then delete the location from staged list
    delete stagedLocations[curr]

    const updatedComponents = {
      ...acc,
      [toLocationChildren[index]]: stagedComponents[curr]
        ? {
            ...stagedComponents[curr],
            location: toLocationChildren[index],
          }
        : undefined,
    }

    // once staged component is moved to new location then delete the component from staged list
    delete stagedComponents[curr]
    delete locations[curr]

    return updatedComponents
  }, {})

  return {
    toLocationChildrenComponents: { ...updatedToLocationChildren },
    updatedStagedLocations: stagedLocations,
    updatedStagedComponents: stagedComponents,
    updatedLocations: locations,
  }
}

const getNewToLocationChildren = (
  toLocationChildren,
  fromLocationChildren,
  components
) =>
  toLocationChildren.reduce(
    (acc, curr, index) => ({
      ...acc,
      // if not toggling componet, ie, nothing previously at toLocation
      [fromLocationChildren[index]]: components[toLocationChildren[index]]
        ? {
            ...components[curr],
            location: fromLocationChildren[index],
          }
        : undefined,
    }),
    {}
  )

const getNewUnassignedFromLocation = (toLocation, fromLocation, components) =>
  components[toLocation]
    ? {
        ...components[toLocation],
        location: fromLocation,
      }
    : undefined

// if toLocation holds plug and from location is unassigned location, add plug to removedComponents list
const updateRemovedComponentsListIfToLocationIsPlug = (
  toLocation,
  isFromUnassigned,
  state
) => {
  if (has(toLocation, get('components', state))) {
    const isToLocationAPlug = getInIncludes(
      ['components', toLocation, 'panelDescription'],
      MSF_CAVITY_PLUG_PART,
      state
    )

    if (isToLocationAPlug && isFromUnassigned) {
      const toLocationComponent = getIn(['components', toLocation], state)
      return compose(
        set(toLocation, toLocationComponent),
        get('removedComponents')
      )(state)
    }
  }

  return get('removedComponents', state)
}

/** End Helpers */
